import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FaTrash } from 'react-icons/fa';
import { format } from 'date-fns';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import debounce from 'debounce-promise';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/auth';
import BoxContent from '../../../components/BoxContent';
import TituloPagina from '../../../components/TituloPagina';
import Modal from '../../../components/Modal';
import FormGroup from '../../../components/form/FormGroup';
import FormDatePicker from '../../../components/form/FormDatePicker';
import Row from '../../../components/form/Row';
import DataTable, { IColumns } from '../../../components/DataTable';
import AsyncSelect from '../../../components/form/AsyncSelect';
import ReactSelect from '../../../components/form/ReactSelect';

interface IUnidade {
  uni_sigla?: string;
  uni_nome?: string;
}

interface ITravaOpmHorasPm {
  id_trava_opm_horas_pm: number;
  uni_codigo: number;
  mes: string;
  ano: string;
  quinzena?: string;
  unidade?: IUnidade;
  criado_em?: string;
}

interface ISchema {
  mesAno: Date;
  quinzena: '1' | '2';
  subunidades: boolean;
}

type OptionType = { label: string; value: string };

type IFilters = {
  type: 'date' | 'select';
  field: string;
  label: string;
  options: OptionType[];
  defaultOption?: string;
  cols: [number, number, number];
};

const anos = Array.from(Array(6).keys())
  .map((i) =>
    i < 5 ? new Date().getFullYear() - i : new Date().getFullYear() + 1,
  )
  .sort()
  .map<OptionType>((ano) => ({ label: ano.toString(), value: ano.toString() }));

const meses = Array.from(Array(12).keys()).map<OptionType>((mes) => ({
  label: (mes + 1).toString().padStart(2, '0'),
  value: (mes + 1).toString().padStart(2, '0'),
}));

const quinzenas: OptionType[] = [
  { label: '1ª (dias 1–15)', value: '1' },
  { label: '2ª (dias 16–31)', value: '2' },
];

const hoje = new Date();
const mesAtual = (hoje.getMonth() + 1).toString().padStart(2, '0');
const quinzenaAtual = hoje.getDate() <= 15 ? '1' : '2';

const defaultFilters: IFilters[] = [
  {
    type: 'select',
    field: 'ano',
    label: 'Ano',
    options: anos,
    defaultOption: hoje.getFullYear().toString(),
    cols: [2, 6, 12],
  },
  {
    type: 'select',
    field: 'mes',
    label: 'Mês',
    options: meses,
    defaultOption: mesAtual,
    cols: [2, 6, 12],
  },
  {
    type: 'select',
    field: 'quinzena',
    label: 'Quinzena',
    options: quinzenas,
    defaultOption: quinzenaAtual,
    cols: [2, 6, 12],
  },
];

const schema = Yup.object().shape({
  mesAno: Yup.date()
    .typeError('Selecione mês/ano')
    .required('Selecione mês/ano'),
  quinzena: Yup.string()
    .required('Selecione a quinzena')
    .oneOf(['1', '2'], 'Selecione a quinzena'),
  subunidades: Yup.boolean(),
});

const ListTravaOpmHorasPm: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();

  const [travaSelecionada, setTravaSelecionada] = useState<ITravaOpmHorasPm>();
  const [reloadKey, setReloadKey] = useState(0);
  const [disabledRequest, setDisabledRequest] = useState(false);
  const [opmSelecionada, setOpmSelecionada] = useState<{
    label: string;
    value: number;
  } | null>(null);
  const [erroOpm, setErroOpm] = useState(false);

  const promiseOpms = useCallback(async (inputValue: string) => {
    try {
      const response = await api.get(`unidades/async?query=${inputValue}`);
      const data = response.data || {};
      return (data.unidades || []).map((item: any) => ({
        value: item.uni_codigo,
        label: item.uni_sigla,
      }));
    } catch (error) {
      return [];
    }
  }, []);

  const delayedOpms = useCallback(
    debounce((query: string) => promiseOpms(query), 500),
    [promiseOpms],
  );

  const {
    isOpen: isAdicionarOpen,
    onOpen: onAdicionarOpen,
    onClose: onAdicionarClose,
  } = useDisclosure();

  const {
    isOpen: isExcluirOpen,
    onOpen: onExcluirOpen,
    onClose: onExcluirClose,
  } = useDisclosure();

  const { handleSubmit, control, errors, reset } = useForm<ISchema>({
    resolver: yupResolver(schema),
    defaultValues: {
      mesAno: undefined,
      quinzena: undefined,
      subunidades: false,
    },
  });

  const opmHeaderCodigo = Number(user.currentOpm?.uni_codigo);

  const refresh = (): void => setReloadKey((k) => k + 1);

  const handleCloseAdicionar = (): void => {
    reset();
    setOpmSelecionada(null);
    setErroOpm(false);
    onAdicionarClose();
  };

  const handleCriarTrava = async ({
    mesAno,
    quinzena,
    subunidades,
  }: ISchema): Promise<void> => {
    if (!opmSelecionada) {
      setErroOpm(true);
      return;
    }
    setErroOpm(false);
    try {
      setDisabledRequest(true);

      const payload: Record<string, unknown> = {
        uni_codigo: opmSelecionada.value,
        mes: format(mesAno, 'MM'),
        ano: format(mesAno, 'yyyy'),
        quinzena,
      };
      if (subunidades) payload.subunidades = '1';

      await api.post('/travaopmhorasm', payload);

      toast({
        title: 'Sucesso.',
        description: 'Trava cadastrada com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });

      handleCloseAdicionar();
      refresh();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Erro ao cadastrar',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setDisabledRequest(false);
    }
  };

  const handleExcluir = async (): Promise<void> => {
    if (!travaSelecionada) return;
    try {
      setDisabledRequest(true);
      await api.delete(
        `/travaopmhorasm/${travaSelecionada.id_trava_opm_horas_pm}`,
      );
      toast({
        title: 'Sucesso.',
        description: 'Trava excluída com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      onExcluirClose();
      refresh();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Erro ao excluir',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setDisabledRequest(false);
    }
  };

  const columns: IColumns = [
    {
      field: 'unidade.uni_sigla',
      text: 'OPM',
      type: { name: 'text' },
    },
    {
      field: 'mes',
      text: 'Mês',
      type: { name: 'text' },
    },
    {
      field: 'ano',
      text: 'Ano',
      type: { name: 'text' },
    },
    {
      field: 'quinzena',
      text: 'Quinzena',
      type: { name: 'text' },
    },
    {
      field: 'criado_em',
      text: 'Criado em',
      type: { name: 'date', format: 'dd/MM/yyyy HH:mm' },
    },
  ];

  const options = {
    serverData: {
      url: `/travaopmhorasm`,
      headers: { Authorization: api.defaults.headers.authorization },
      serverPagination: true,
      params: `opm=${opmHeaderCodigo}`,
    },
    filters: defaultFilters,
    actions: {
      headerText: 'Ações',
      items: [
        {
          icon: <FaTrash size={13} />,
          tooltip: 'Excluir',
          getRow: (trava: ITravaOpmHorasPm) => {
            setTravaSelecionada(trava);
            onExcluirOpen();
          },
        },
      ],
    },
  };

  return (
    <>
      <TituloPagina title="Trava OPM Horas PM" />
      <BoxContent>
        <Flex justify="flex-end" mb={4}>
          <Button colorScheme="blue" onClick={onAdicionarOpen}>
            Adicionar exceção
          </Button>
        </Flex>

        <Box mt={4}>
          <DataTable key={reloadKey} columns={columns} options={options} />
        </Box>
      </BoxContent>

      <Modal
        isOpen={isAdicionarOpen}
        onClose={handleCloseAdicionar}
        size="lg"
        title="Adicionar exceção de trava de horas PM"
      >
        <Box as="form" onSubmit={handleSubmit(handleCriarTrava)}>
          <Row>
            <FormGroup name="OPM" required cols={[12, 12, 12]}>
              <AsyncSelect
                name="uni_codigo"
                label="Pesquisar OPM"
                value={opmSelecionada}
                loadOptions={(value: any) => delayedOpms(value)}
                onChange={(value: any) => {
                  setOpmSelecionada(value);
                  if (value) setErroOpm(false);
                }}
                error={erroOpm ? 'Campo obrigatório' : undefined}
              />
            </FormGroup>
          </Row>

          <Row>
            <FormGroup name="Mês/Ano" required cols={[6, 12, 12]}>
              <Controller
                control={control}
                name="mesAno"
                render={({ onChange, value }) => (
                  <FormDatePicker
                    selected={value}
                    onChange={(date) => onChange(date as Date)}
                    dateFormat="MM/yyyy"
                    showMonthYearPicker
                    error={errors.mesAno?.message}
                  />
                )}
              />
            </FormGroup>

            <FormGroup name="Quinzena" required cols={[6, 12, 12]}>
              <Controller
                control={control}
                name="quinzena"
                render={({ onChange, value }) => (
                  <ReactSelect
                    optionsSelect={quinzenas}
                    value={
                      quinzenas.find((option) => option.value === value) || null
                    }
                    onChange={(option: OptionType) => onChange(option.value)}
                    error={errors.quinzena?.message}
                  />
                )}
              />
            </FormGroup>
          </Row>

          <Row>
            <FormGroup cols={[6, 12, 12]}>
              <Controller
                control={control}
                name="subunidades"
                render={({ onChange, value }) => (
                  <Checkbox
                    isChecked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                  >
                    Incluir subunidades
                  </Checkbox>
                )}
              />
            </FormGroup>
          </Row>

          <Flex mt={6} justify="flex-end">
            <HStack spacing={3}>
              <Button onClick={handleCloseAdicionar} variant="outline">
                Cancelar
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={disabledRequest}
              >
                Salvar
              </Button>
            </HStack>
          </Flex>
        </Box>
      </Modal>

      {travaSelecionada && (
        <Modal
          isOpen={isExcluirOpen}
          onClose={onExcluirClose}
          size="lg"
          title="Excluir trava de horas PM"
        >
          <Text>
            Confirma a exclusão da trava da OPM{' '}
            <strong>
              {travaSelecionada.unidade?.uni_sigla ||
                travaSelecionada.uni_codigo}
            </strong>{' '}
            para{' '}
            <strong>
              {travaSelecionada.mes}/{travaSelecionada.ano}
            </strong>
            {travaSelecionada.quinzena && (
              <> ({travaSelecionada.quinzena}ª quinzena)</>
            )}
            ?
          </Text>
          <Flex mt={6} justify="center">
            <HStack spacing={3}>
              <Button onClick={onExcluirClose} colorScheme="green">
                Não
              </Button>
              <Button
                onClick={handleExcluir}
                colorScheme="red"
                isLoading={disabledRequest}
              >
                Sim, excluir
              </Button>
            </HStack>
          </Flex>
        </Modal>
      )}
    </>
  );
};

export default ListTravaOpmHorasPm;
